import { useNavigate } from 'react-router-dom';
import { ORAL_MICROBIOME_PATH, GUT_MICROBIOME_PATH } from '@/constants/paths';
import { MicrobiomeProfileCard } from '@design-system/Cards/MicrobiomeProfileCard';
import { Skeleton } from '@design-system/Skeleton';
import { useGetMyMicrobiomeProfilesV2 } from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { MicrobiomeProfileResponse } from '@viome/viome-base/src/usecase/Health/Health.model';
import OralMicrobiome from '@/assets/microbiome_oral.json';
import GutMicrobiome from '@/assets/microbiome_gut.json';
import { EventTracker } from '@/utils';

export const MicrobiomeProfileSection = () => {
  const { data, isLoading, isError } = useGetMyMicrobiomeProfilesV2();

  const navigate = useNavigate();

  const navigateToMicrobiome = (id: string) => {
    switch (id) {
      case 'gut':
        EventTracker.trackEvent(
          'health',
          'health_myMicrobiomeProfile_gutMicrobiome',
        );
        return navigate(`${GUT_MICROBIOME_PATH}`);
      case 'oral':
        EventTracker.trackEvent(
          'health',
          'health_myMicrobiomeProfile_oralMicrobiome',
        );
        return navigate(`${ORAL_MICROBIOME_PATH}`);
      default:
        return navigate(`${GUT_MICROBIOME_PATH}`);
    }
  };

  if (isError) {
    return;
  }

  if (isLoading || !data) {
    return (
      <div className="mt-12">
        <Skeleton className="h-9 w-[50%] mb-4 rounded-lg" />
        <Skeleton className="h-5 w-[80%] mb-4 rounded-lg" />
        <div className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1 mt-6">
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              className="h-16 w-full rounded-lg"
            />
          ))}
        </div>
      </div>
    );
  }

  if (data && data.length <= 0) {
    return;
  }

  const microbiomeProfile: MicrobiomeProfileResponse = data;
  return (
    <div className="mt-12" data-testid="health-microbiome-section">
      <h2 className="dark typography-h1 text-primary mb-2" data-testid="health-microbiome-title">
        My Microbiome Profile
      </h2>
      <p className="dark typography-body text-secondary mb-6 lg:w-[35rem]" data-testid="health-microbiome-description">
        Get to know the ones who call you home—your microbes, the architects of
        your health.
      </p>
      <div className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1" data-testid="health-microbiome-grid">
        {microbiomeProfile?.map((microbiome) => (
          <div
            key={`${microbiome.id}-microbiome-card`}
            data-testid={`health-microbiome-card-${microbiome.id}`}
          >
            <MicrobiomeProfileCard
              title={microbiome.title}
              mediaUrl={
                microbiome.title == 'Oral Microbiome'
                  ? OralMicrobiome
                  : GutMicrobiome
              }
              data-testid={`health-microbiome-profileCard-${microbiome.id}`}
              onClick={() => navigateToMicrobiome(microbiome.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
